/**
 * Предустановка: Сценарий для демонстрации rebase
 * Используется для уроков по переписыванию истории
 */

import type { GitPreset } from './types';

/** Проект для демонстрации rebase операций */
export const rebaseScenarioPreset: GitPreset = {
  id: 'rebase-scenario',
  name: 'Rebase демо',
  description: 'Проект с двумя ветками для демонстрации rebase вместо merge',

  currentBranch: 'feature/new-feature',

  branches: [
    { name: 'main', head: 'c4', color: '#F05032' },
    { name: 'feature/new-feature', head: 'c6', color: '#4a9eff' },
  ],

  commits: [
    // Main branch
    {
      sha: 'c1',
      message: 'Initial commit: Project setup',
      parents: [],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704067200, // 2024-01-01
      files: ['README.md', 'package.json'],
    },
    {
      sha: 'c2',
      message: 'Add base modules',
      parents: ['c1'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704153600, // 2024-01-02
      files: ['src/index.ts', 'src/utils.ts'],
    },
    {
      sha: 'c3',
      message: 'Add configuration system',
      parents: ['c2'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704240000, // 2024-01-03
      files: ['src/config.ts', 'src/types.ts'],
    },
    {
      sha: 'c4',
      message: 'Update dependencies and add tests',
      parents: ['c3'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704326400, // 2024-01-04
      files: ['package.json', 'tests/utils.test.ts'],
    },
    // Feature branch (отходит от c2)
    {
      sha: 'c5',
      message: 'Start new feature implementation',
      parents: ['c2'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704240000, // 2024-01-03 (параллельно с c3)
      files: ['src/feature.ts'],
    },
    {
      sha: 'c6',
      message: 'Complete feature with helper functions',
      parents: ['c5'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704326400, // 2024-01-04 (параллельно с c4)
      files: ['src/feature.ts', 'src/helpers.ts'],
    },
  ],

  files: [
    {
      path: 'README.md',
      content: `# Feature Development Project

Проект для демонстрации rebase.

## Сценарий

1. Ветка \`feature/new-feature\` отходит от c2
2. В \`main\` добавлены новые коммиты (c3, c4)
3. Нужно выполнить rebase feature на main

## Команды

\`\`\`bash
git checkout feature/new-feature
git rebase main
\`\`\`
`,
      status: 'committed',
    },
    {
      path: 'package.json',
      content: `{
  "name": "feature-project",
  "version": "1.0.0",
  "description": "Project for rebase demonstration",
  "main": "src/index.ts",
  "scripts": {
    "start": "ts-node src/index.ts",
    "test": "jest"
  },
  "dependencies": {
    "typescript": "^5.0.0"
  },
  "devDependencies": {
    "@types/node": "^20.0.0",
    "jest": "^29.0.0",
    "ts-node": "^10.0.0"
  }
}
`,
      status: 'committed',
    },
    {
      path: 'src/index.ts',
      content: `import { greet, formatDate } from './utils';
import { config } from './config';

console.log(greet('World'));
console.log('Config:', config);
console.log('Today:', formatDate(new Date()));
`,
      status: 'committed',
    },
    {
      path: 'src/utils.ts',
      content: `/**
 * Utility functions
 */

export function greet(name: string): string {
  return \`Hello, \${name}!\`;
}

export function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
`,
      status: 'committed',
    },
    {
      path: 'src/config.ts',
      content: `import type { AppConfig } from './types';

export const config: AppConfig = {
  appName: 'Feature Project',
  version: '1.0.0',
  debug: false,
  features: {
    newFeature: true,
    experimentalUI: false,
  },
};
`,
      status: 'committed',
    },
    {
      path: 'src/types.ts',
      content: `export interface AppConfig {
  appName: string;
  version: string;
  debug: boolean;
  features: {
    newFeature: boolean;
    experimentalUI: boolean;
  };
}

export interface FeatureResult {
  success: boolean;
  data?: unknown;
  error?: string;
}
`,
      status: 'committed',
    },
    {
      path: 'src/feature.ts',
      content: `import type { FeatureResult } from './types';
import { capitalize } from './utils';

/**
 * New feature implementation
 * Этот файл будет изменён после rebase
 */

export function processFeature(input: string): FeatureResult {
  try {
    const processed = capitalize(input);
    return {
      success: true,
      data: {
        original: input,
        processed,
        timestamp: Date.now(),
      },
    };
  } catch (error) {
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

export function validateFeature(input: unknown): boolean {
  return typeof input === 'string' && input.length > 0;
}
`,
      status: 'committed',
    },
    {
      path: 'src/helpers.ts',
      content: `/**
 * Helper functions for the new feature
 */

export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export function throttle<T extends (...args: unknown[]) => unknown>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export function memoize<T extends (...args: unknown[]) => unknown>(fn: T): T {
  const cache = new Map<string, ReturnType<T>>();

  return ((...args: Parameters<T>) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = fn(...args) as ReturnType<T>;
    cache.set(key, result);
    return result;
  }) as T;
}
`,
      status: 'committed',
    },
    {
      path: 'tests/utils.test.ts',
      content: `import { greet, formatDate, capitalize } from '../src/utils';

describe('Utils', () => {
  test('greet should return greeting', () => {
    expect(greet('World')).toBe('Hello, World!');
  });

  test('formatDate should return ISO date string', () => {
    const date = new Date('2024-01-01');
    expect(formatDate(date)).toBe('2024-01-01');
  });

  test('capitalize should capitalize first letter', () => {
    expect(capitalize('hello')).toBe('Hello');
    expect(capitalize('WORLD')).toBe('WORLD');
  });
});
`,
      status: 'committed',
    },
  ],

  visualization: {
    branchColors: {
      main: '#F05032',
      'feature/new-feature': '#4a9eff',
    },
    nodePositions: {
      c1: { x: 100, y: 200 },
      c2: { x: 200, y: 200 },
      c3: { x: 300, y: 200 },
      c4: { x: 400, y: 200 },
      c5: { x: 300, y: 300 },
      c6: { x: 400, y: 300 },
    },
    annotations: [
      { sha: 'c2', text: 'Точка ветвления', position: 'bottom' },
      { sha: 'c4', text: 'main HEAD', position: 'top' },
      { sha: 'c6', text: 'feature HEAD (до rebase)', position: 'right' },
    ],
    highlightedCommits: ['c4', 'c6'],
  },
};

/**
 * Состояние после rebase
 * Feature коммиты будут иметь новые SHA
 */
export const afterRebaseState = {
  description: 'После git rebase main, коммиты c5 и c6 будут пересозданы поверх c4',
  newCommits: [
    {
      sha: "c5'", // Новый SHA после rebase
      message: 'Start new feature implementation',
      parents: ['c4'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704240000,
      files: ['src/feature.ts'],
    },
    {
      sha: "c6'", // Новый SHA после rebase
      message: 'Complete feature with helper functions',
      parents: ["c5'"],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704326400,
      files: ['src/feature.ts', 'src/helpers.ts'],
    },
  ],
  visualization: {
    nodePositions: {
      c1: { x: 100, y: 200 },
      c2: { x: 200, y: 200 },
      c3: { x: 300, y: 200 },
      c4: { x: 400, y: 200 },
      "c5'": { x: 500, y: 200 },
      "c6'": { x: 600, y: 200 },
    },
    annotations: [
      { sha: 'c4', text: 'main', position: 'top' },
      { sha: "c6'", text: 'feature (после rebase)', position: 'right' },
    ],
  },
};
